/**
 * Dashboard Login Component
 *
 * Simplified login page that redirects to backend /auth/login endpoint.
 * Backend handles OIDC flow with Microsoft Entra External ID (CIAM).
 *
 * Flow:
 * 1. Check if already authenticated via /auth/status
 * 2. If authenticated, navigate to dashboard
 * 3. If not authenticated, show login button
 * 4. On login, redirect to backend /auth/login (which redirects to CIAM)
 * 5. Handle error messages from query parameters
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { API_BASE_URL } from '../../../config/api';
import { setDashboardTitle, restoreBaseTitle } from '../pageTitles';

const DashboardLogin = () => {
  // Outside DashboardLayout, so it sets its own tab title.
  useEffect(() => {
    setDashboardTitle('Sign in');
    return restoreBaseTitle;
  }, []);

  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const returnUrl = searchParams.get('returnUrl') || '/dashboard';
  const error = searchParams.get('error');

  useEffect(() => {
    // Check if already authenticated
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Check if user is already authenticated
   */
  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/status`, {
        credentials: 'include',
      });

      const data = await response.json();

      if (data.authenticated) {
        // Already logged in, redirect to dashboard
        console.log('[DashboardLogin] Already authenticated, redirecting to:', returnUrl);
        navigate(returnUrl);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('[DashboardLogin] Auth check failed:', err);
      setLoading(false);
    }
  };

  /**
   * Initiate login flow by redirecting to backend /auth/login
   */
  const handleLogin = () => {
    console.log('[DashboardLogin] Redirecting to backend auth, returnUrl:', returnUrl);
    window.location.href = `${API_BASE_URL}/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  /**
   * Get error message based on error code
   */
  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth_failed':
        return 'Authentication failed. Please try again.';
      case 'invalid_state':
        return 'Invalid session. Please try again.';
      case 'token_failed':
        return 'Token exchange failed. Please try again.';
      default:
        return 'An error occurred. Please try again.';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">AI</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Checking authentication...</h1>
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to Main Site */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center space-x-2 text-white/80 hover:text-white mb-8 transition-colors"
        >
          <FiArrowLeft size={20} />
          <span>Back to Main Site</span>
        </button>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-2xl">AI</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              NuVola Dashboard
            </h1>
            <p className="text-gray-600">
              Sign in to access your account
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6">
              <p className="font-medium">Authentication Error</p>
              <p className="text-sm mt-1">{getErrorMessage(error)}</p>
            </div>
          )}

          {/* Login Info */}
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-6">
            <p className="text-sm">
              <strong>Secure Sign-In:</strong> You'll be redirected to Microsoft's secure login page to authenticate.
            </p>
          </div>

          {/* Sign In Button */}
          <button
            onClick={handleLogin}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all flex items-center justify-center space-x-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 0H0v10h10V0zM20 0H10v10h10V0zM10 10H0v10h10V10zM20 10H10v10h10V10z"/>
            </svg>
            <span>Sign in with Microsoft</span>
          </button>

          {/* Footer Note */}
          <div className="mt-6 text-center text-xs text-gray-500">
            <p>Secure authentication powered by</p>
            <p className="font-medium">Microsoft Entra External ID</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardLogin;
